import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Sparkles, Radio, Clock, QrCode, UtensilsCrossed, MessageSquare, ListMusic,
  ChevronDown, Loader2, CalendarClock, Sliders, Disc3, Layers,
} from 'lucide-react';
import { Button } from './ui/button';
import AiScheduleEditor from './AiScheduleEditor';
import CommercialScheduleEditor from './CommercialScheduleEditor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ENERGY_ACCENT = { chill: '#7DD3FC', upbeat: '#39FF14', groovy: '#FFB300', dance: '#FF007F' };

const Metric = ({ icon: Icon, label, value, testid }) => (
  <div className="bg-black/30 border border-white/5 rounded-xl px-3 py-2.5 flex-1 min-w-[84px]" data-testid={testid}>
    <div className="flex items-center gap-1.5 text-gray-500 text-[10px] uppercase tracking-wider mb-0.5">
      <Icon className="w-3 h-3" /> {label}
    </div>
    <div className="text-white font-extrabold text-lg leading-none">{value}</div>
  </div>
);

// ── Editors (target either an outlet or a zone via basePath) ──────────────
const AiVibeEditor = ({ basePath, onChanged }) => {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    let on = true;
    axios.get(`${API}${basePath}/ai-mode`).then((r) => { if (on) setData(r.data); }).catch(() => {});
    return () => { on = false; };
  }, [basePath]);
  if (!data) return <div className="py-3 flex justify-center"><Loader2 className="w-4 h-4 text-gray-500 animate-spin" /></div>;
  const isManual = data.mode === 'manual';
  const curGenre = (isManual ? (data.manual_genres?.[0]) : data.baseline_genre) || data.genres[0];
  const curEnergy = isManual ? data.manual_energy : data.baseline_energy;
  const save = async (patch) => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}${basePath}/ai-mode`, patch);
      setData((p) => ({ ...p, ...r.data }));
      toast.success('Updated');
      onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not update'); }
    finally { setSaving(false); }
  };
  const setGenre = (g) => save(isManual ? { manual_genres: [g] } : { baseline_genre: g });
  const setEnergy = (e) => save(isManual ? { manual_energy: e } : { baseline_energy: e });
  return (
    <div className="bg-black/20 border border-white/5 rounded-2xl p-4 space-y-3" data-testid="ai-vibe-editor">
      <div className="flex items-center gap-2">
        <span className="text-[11px] text-gray-500 uppercase tracking-wider w-14">Mode</span>
        <div className="flex gap-1.5">
          {['auto', 'manual'].map((m) => (
            <button key={m} onClick={() => save({ mode: m })} disabled={saving}
              data-testid={`vibe-mode-${m}`}
              className={`px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider transition-all ${data.mode === m ? 'bg-[#00FFFF] text-black' : 'bg-white/5 text-gray-400 hover:bg-white/10'}`}>{m}</button>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-[11px] text-gray-500 uppercase tracking-wider w-14">Genre</span>
        <select value={curGenre} onChange={(e) => setGenre(e.target.value)} disabled={saving} style={{ colorScheme: 'dark' }}
          className="flex-1 bg-black/40 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white capitalize focus:outline-none focus:border-[#39FF14]/50"
          data-testid="vibe-genre-select">
          {data.genres.map((g) => <option key={g} value={g}>{g.replace(/-/g, ' ')}</option>)}
        </select>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-[11px] text-gray-500 uppercase tracking-wider w-14">Energy</span>
        <div className="flex gap-1.5 flex-wrap">
          {data.energies.map((e) => (
            <button key={e} onClick={() => setEnergy(e)} disabled={saving} data-testid={`vibe-energy-${e}`}
              className="px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider transition-all"
              style={curEnergy === e ? { background: ENERGY_ACCENT[e], color: '#000' } : { background: 'rgba(255,255,255,0.05)', color: '#9ca3af' }}>{e}</button>
          ))}
        </div>
      </div>
    </div>
  );
};

const AiSchedulePanel = ({ basePath }) => {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    let on = true;
    axios.get(`${API}${basePath}/ai-schedule`).then((r) => { if (on) setData(r.data); }).catch(() => {});
    return () => { on = false; };
  }, [basePath]);
  if (!data) return <div className="py-3 flex justify-center"><Loader2 className="w-4 h-4 text-gray-500 animate-spin" /></div>;
  const onSave = async (enabled, schedule) => {
    setSaving(true);
    try { const r = await axios.put(`${API}${basePath}/ai-schedule`, { enabled, schedule }); return r.data; }
    finally { setSaving(false); }
  };
  return <AiScheduleEditor enabled={data.enabled} schedule={data.schedule} genres={data.genres || []} energies={data.energies || []} onSave={onSave} saving={saving} />;
};

const CommercialSchedulePanel = ({ basePath }) => {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    let on = true;
    axios.get(`${API}${basePath}/commercial-schedule`).then((r) => { if (on) setData(r.data); }).catch(() => {});
    return () => { on = false; };
  }, [basePath]);
  if (!data) return <div className="py-3 flex justify-center"><Loader2 className="w-4 h-4 text-gray-500 animate-spin" /></div>;
  const onSave = async (selection) => {
    setSaving(true);
    try { const r = await axios.put(`${API}${basePath}/commercial-schedule`, { selection }); return r.data; }
    finally { setSaving(false); }
  };
  return <CommercialScheduleEditor selection={data.selection} playlists={data.playlists || []} onSave={onSave} saving={saving} />;
};

// Shared actions row (Change vibe + Timetable) + expanded panels.
const VibeAndSchedule = ({ basePath, isAi, onChanged, idSuffix }) => {
  const [panel, setPanel] = useState(null);
  const toggle = (p) => setPanel((cur) => (cur === p ? null : p));
  return (
    <>
      <div className="flex flex-wrap gap-2">
        {isAi && (
          <Button type="button" onClick={() => toggle('vibe')} variant="ghost"
            className={`h-9 px-4 rounded-full text-xs font-bold border ${panel === 'vibe' ? 'border-[#00FFFF] text-[#00FFFF] bg-[#00FFFF]/10' : 'border-white/10 text-gray-200 hover:bg-white/10'}`}
            data-testid={`toggle-vibe-${idSuffix}`}>
            <Sliders className="w-4 h-4 mr-1.5" /> Change vibe <ChevronDown className={`w-3.5 h-3.5 ml-1 transition-transform ${panel === 'vibe' ? 'rotate-180' : ''}`} />
          </Button>
        )}
        <Button type="button" onClick={() => toggle('schedule')} variant="ghost"
          className={`h-9 px-4 rounded-full text-xs font-bold border ${panel === 'schedule' ? 'border-[#39FF14] text-[#39FF14] bg-[#39FF14]/10' : 'border-white/10 text-gray-200 hover:bg-white/10'}`}
          data-testid={`toggle-schedule-${idSuffix}`}>
          <CalendarClock className="w-4 h-4 mr-1.5" /> Timetable <ChevronDown className={`w-3.5 h-3.5 ml-1 transition-transform ${panel === 'schedule' ? 'rotate-180' : ''}`} />
        </Button>
      </div>
      {panel === 'vibe' && isAi && <div className="mt-4"><AiVibeEditor basePath={basePath} onChanged={onChanged} /></div>}
      {panel === 'schedule' && (
        <div className="mt-4">{isAi ? <AiSchedulePanel basePath={basePath} /> : <CommercialSchedulePanel basePath={basePath} />}</div>
      )}
    </>
  );
};

const StatusPill = ({ online, testid }) => (
  <div className={`flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full flex-shrink-0 ${online ? 'bg-[#39FF14]/15 text-[#39FF14]' : 'bg-white/5 text-gray-500'}`} data-testid={testid}>
    <span className={`w-1.5 h-1.5 rounded-full ${online ? 'bg-[#39FF14] animate-pulse' : 'bg-gray-600'}`} />
    {online ? 'LIVE' : 'OFFLINE'}
  </div>
);

const NowLine = ({ isAi, online, ai, np }) => (
  isAi ? (
    <p className="text-sm text-white font-semibold truncate">
      <span className="capitalize">{ai?.resolved_genre?.replace(/-/g, ' ') || '—'}</span>
      <span className="mx-1.5 text-gray-600">·</span>
      <span className="uppercase" style={{ color: ENERGY_ACCENT[ai?.resolved_energy] || '#fff' }}>{ai?.resolved_energy || '—'}</span>
      {ai?.schedule_enabled && <span className="ml-2 text-[9px] text-[#00FFFF] uppercase">scheduled</span>}
    </p>
  ) : (
    <p className="text-sm text-white font-semibold truncate">{np ? `${np.title}${np.artist ? ` — ${np.artist}` : ''}` : (online ? 'Idle' : 'Nothing playing')}</p>
  )
);

// ── One zone row inside a multi-zone outlet card ──────────────────────────
const ZoneRow = ({ zone, onChanged }) => {
  const isAi = zone.mode === 'ai';
  return (
    <div className="border border-white/8 rounded-2xl p-4 bg-black/20" data-testid={`owner-zone-${zone.id}`}>
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-white font-bold truncate" data-testid={`zone-name-${zone.id}`}>{zone.name}</span>
          {zone.is_main && <span className="text-[9px] uppercase tracking-wider text-gray-500 bg-white/5 px-1.5 py-0.5 rounded">main</span>}
          <span className="text-[9px] uppercase tracking-wider text-gray-500">{isAi ? 'AI' : 'Commercial'}</span>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <span className="text-[11px] text-gray-400 flex items-center gap-1"><Clock className="w-3 h-3" />{zone.hours_today}h today</span>
          <StatusPill online={zone.online} testid={`zone-status-${zone.id}`} />
        </div>
      </div>
      <div className="flex items-center gap-2 mb-3 min-w-0">
        <Disc3 className={`w-4 h-4 flex-shrink-0 ${zone.online ? 'text-[#00FFFF]' : 'text-gray-600'}`} />
        <NowLine isAi={isAi} online={zone.online} ai={zone.ai} np={zone.now_playing} />
      </div>
      <VibeAndSchedule basePath={`/owner/zones/${zone.id}`} isAi={isAi} onChanged={onChanged} idSuffix={zone.id} />
    </div>
  );
};

export const OwnerOutletCard = ({ outlet, onChanged }) => {
  const [expanded, setExpanded] = useState(false);
  const isAi = outlet.client_type === 'ai';
  const multi = !!outlet.multi_zone;

  const MetricTiles = () => (
    <div className="flex flex-wrap gap-2 mb-4">
      <Metric icon={Clock} label={multi ? 'Hrs today' : 'Session hrs'} value={multi ? outlet.hours_today_total : outlet.session_hours} testid={`metric-hours-${outlet.id}`} />
      <Metric icon={QrCode} label="Scans" value={outlet.metrics.scans} testid={`metric-scans-${outlet.id}`} />
      <Metric icon={UtensilsCrossed} label="Menu views" value={outlet.metrics.menu_views} testid={`metric-menu-${outlet.id}`} />
      {!isAi && <Metric icon={ListMusic} label="Requests" value={outlet.metrics.requests ?? 0} testid={`metric-requests-${outlet.id}`} />}
      <Metric icon={MessageSquare} label="Shoutouts" value={outlet.metrics.shoutouts} testid={`metric-shoutouts-${outlet.id}`} />
    </div>
  );

  const Header = ({ right }) => (
    <div className="flex items-start justify-between gap-3 mb-4">
      <div className="flex items-center gap-3 min-w-0">
        <div className={`w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0 ${isAi ? 'bg-[#00FFFF]/15 ring-1 ring-[#00FFFF]/40' : 'bg-[#FF007F]/15 ring-1 ring-[#FF007F]/40'}`}>
          {isAi ? <Sparkles className="w-5 h-5 text-[#00FFFF]" /> : <Radio className="w-5 h-5 text-[#FF007F]" />}
        </div>
        <div className="min-w-0">
          <h3 className="text-lg font-extrabold text-white truncate" data-testid={`outlet-name-${outlet.id}`}>{outlet.name}</h3>
          <p className="text-[11px] uppercase tracking-wider text-gray-500">{isAi ? 'AI Venue' : 'Commercial'}{outlet.venue_code ? ` · ${outlet.venue_code}` : ''}</p>
        </div>
      </div>
      {right}
    </div>
  );

  // ── Multi-zone outlet: expandable card, one row per zone ────────────────
  if (multi) {
    const zones = outlet.zones || [];
    return (
      <div className="bg-white/[0.03] border border-white/10 rounded-3xl p-5 lg:p-6" data-testid={`owner-outlet-${outlet.id}`}>
        <Header right={(
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full bg-[#39FF14]/15 text-[#39FF14]" data-testid={`zones-live-${outlet.id}`}>
              <span className="w-1.5 h-1.5 rounded-full bg-[#39FF14] animate-pulse" /> {outlet.zones_live}/{zones.length} live
            </span>
          </div>
        )} />
        <MetricTiles />
        <button onClick={() => setExpanded((e) => !e)} className="w-full flex items-center justify-between text-xs font-bold text-gray-300 bg-black/30 border border-white/10 rounded-xl px-4 py-2.5 hover:bg-white/5 transition-all" data-testid={`expand-zones-${outlet.id}`}>
          <span className="flex items-center gap-2"><Layers className="w-4 h-4 text-[#00FFFF]" /> {zones.length} zones · {outlet.hours_today_total}h today</span>
          <ChevronDown className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </button>
        {expanded && (
          <div className="mt-3 space-y-3" data-testid={`zones-list-${outlet.id}`}>
            {zones.map((z) => <ZoneRow key={z.id} zone={z} onChanged={onChanged} />)}
          </div>
        )}
      </div>
    );
  }

  // ── Single-outlet card (unchanged; the word "zone" never appears) ───────
  const np = outlet.now_playing;
  return (
    <div className="bg-white/[0.03] border border-white/10 rounded-3xl p-5 lg:p-6" data-testid={`owner-outlet-${outlet.id}`}>
      <Header right={<StatusPill online={outlet.online} testid={`outlet-status-${outlet.id}`} />} />
      <div className="mb-4 flex items-center gap-3 bg-black/30 border border-white/5 rounded-2xl p-3" data-testid={`outlet-nowplaying-${outlet.id}`}>
        <Disc3 className={`w-5 h-5 flex-shrink-0 ${outlet.online ? 'text-[#00FFFF] animate-spin' : 'text-gray-600'}`} style={{ animationDuration: '3s' }} />
        <div className="min-w-0">
          <p className="text-[10px] text-gray-500 uppercase tracking-wider">{isAi ? 'Now playing · genre / energy' : 'Now playing'}</p>
          <NowLine isAi={isAi} online={outlet.online} ai={outlet.ai} np={np} />
        </div>
      </div>
      <MetricTiles />
      <VibeAndSchedule basePath={`/owner/outlets/${outlet.id}`} isAi={isAi} onChanged={onChanged} idSuffix={outlet.id} />
    </div>
  );
};

export default OwnerOutletCard;
